/**
 * Mitarbeiter-Ansicht des Kundenportals öffnen — für Techniker, Teamleads und Admins.
 *
 * Das Fenster geht SOFORT im Klick auf und wird erst nach der Server-Antwort auf die
 * Portal-URL gelenkt: Ein `window.open` nach einem `await` werten Browser als Popup ohne
 * Nutzeraktion und blocken es. Klappt es trotzdem nicht, gibt es den Link zum Anklicken.
 */
import { Anchor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { PortalAreaValue } from '../lib/portalAccessTypes';

/** Angenommene Auswahl: das Portal so zeigen, wie es mit genau diesen Bereichen aussähe. */
export type PortalPreviewAssume = PortalAreaValue;

interface PreviewVars {
  contactId: string;
  contactName: string;
  win: Window | null;
  assume?: PortalPreviewAssume;
}

export function useCustomerPortalPreview() {
  const mutation = useMutation({
    mutationFn: (vars: PreviewVars) => api.startCustomerPortalPreview(vars.contactId, vars.assume),
    onSuccess: ({ url }, { win, contactName }) => {
      if (win && !win.closed) {
        win.location.href = url;
        return;
      }
      notifications.show({
        title: 'Neues Fenster wurde blockiert',
        message: (
          <Anchor href={url} target="_blank" rel="noopener noreferrer">
            Kundenportal als {contactName} öffnen
          </Anchor>
        ),
        color: 'orange',
        autoClose: 60_000,
      });
    },
    onError: (e: Error, { win }) => {
      win?.close();
      notifications.show({ title: 'Mitarbeiter-Ansicht nicht möglich', message: e.message, color: 'red' });
    },
  });

  const open = (contact: { id: string; name: string }, assume?: PortalPreviewAssume) => {
    const win = window.open('', '_blank');
    if (win) {
      // Die Portal-Seite braucht keinen Zugriff zurück auf den ServiceHub-Tab.
      win.opener = null;
      try {
        win.document.title = 'Kundenportal wird geöffnet …';
        win.document.body.textContent = 'Kundenportal wird geöffnet …';
      } catch {
        /* reine Kosmetik */
      }
    }
    mutation.mutate({ contactId: contact.id, contactName: contact.name, win, assume });
  };

  return {
    open,
    /** Kontakt, dessen Ansicht gerade vorbereitet wird — für den Lade-Zustand am Knopf. */
    pendingContactId: mutation.isPending ? (mutation.variables?.contactId ?? null) : null,
  };
}
